import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion, useReducedMotion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    'transition-all duration-fast disabled:opacity-40 disabled:pointer-events-none',
    // Accessible focus ring — always visible, 2px brand outline with 2px offset
    'focus-visible:outline-2 focus-visible:outline-brand-default focus-visible:outline-offset-2',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-brand-default text-white hover:bg-brand-hover active:bg-brand-active focus-visible:shadow-glow',
        secondary:
          'bg-bg-muted text-text-primary hover:bg-bg-emphasis',
        ghost:
          'bg-transparent hover:bg-bg-subtle text-text-secondary',
        danger:
          'bg-danger-default text-white hover:bg-red-600 active:bg-red-700',
        // backward-compat alias
        destructive:
          'bg-danger-default text-white hover:bg-red-600 active:bg-red-700',
        outline:
          'border border-border-default bg-transparent text-text-primary hover:bg-bg-subtle',
        link:
          'bg-transparent text-brand-default hover:underline underline-offset-4 h-auto p-0 rounded-none',
      },
      size: {
        sm:   'h-8 px-3 text-sm',
        md:   'h-9 px-4 text-sm',
        lg:   'h-10 px-5 text-base',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, isLoading = false, disabled, children, ...props },
    ref,
  ) => {
    const shouldReduce = useReducedMotion();
    const computedClass = cn(buttonVariants({ variant, size }), className);

    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={computedClass}
          aria-disabled={disabled || isLoading}
          aria-busy={isLoading || undefined}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    const motionProps = props as unknown as React.ComponentPropsWithoutRef<typeof motion.button>;

    return (
      <motion.button
        {...motionProps}
        ref={ref}
        className={computedClass}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...(shouldReduce || disabled || isLoading ? {} : { whileTap: { scale: 0.97 } })}
        transition={{ duration: 0.1 }}
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {children}
      </motion.button>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
